import { isDesktopRuntime } from './desktopDetection';

/** A page error held before the desktop's forwarder is in: a `window` `error` or `unhandledrejection`. */
export type EarlyPageErrorType = ErrorEvent | PromiseRejectionEvent;

/** At most this many are held; the forwarder limits what it sends again on its own. */
export const EARLY_PAGE_ERRORS_HELD = 20;

/**
 * T-0896 (LC-6i): the desktop's error forwarding loads behind `desktop/desktopBoot.ts`, one chunk after
 * the entry. F-0689 DG-8 still holds from the first module on: this small listener, in the entry, holds
 * what the page raises until the forwarder is in, and hands it over with `release()`, which stops
 * listening. Outside the desktop app it listens to nothing and releases nothing.
 */
export function holdEarlyPageErrors(
  target: Window = window,
  desktop: boolean = isDesktopRuntime(),
): () => EarlyPageErrorType[] {
  if (!desktop) return () => [];
  const held: EarlyPageErrorType[] = [];
  const hold = (event: Event) => {
    if (held.length < EARLY_PAGE_ERRORS_HELD) held.push(event as EarlyPageErrorType);
  };
  target.addEventListener('error', hold);
  target.addEventListener('unhandledrejection', hold);
  return () => {
    target.removeEventListener('error', hold);
    target.removeEventListener('unhandledrejection', hold);
    return held.splice(0);
  };
}
